import { css } from '@emotion/react';
import { useCallback } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { KpiSection } from '../../../../data-models/company-financials.data-model';
import { SortableItem } from '../../../../components/SortableList/SortableItem';
import {
  CustomKpiTemplateFormValues,
  CustomTemplateSectionType,
  TemplateSectionFormValues,
} from '../../../../schemas/kpi-template.schema';
import { isSectionToggleDisabled } from './section-utils';
import { SectionContent } from './sections/SectionContent';
import { SectionHeader } from './sections/SectionHeader';
import { SingleKpiSectionRow } from './sections/SingleKpiSectionRow';
import { sectionCardCss } from './sections/section-editor.styles';

const cardCss = css(sectionCardCss);

interface TemplateSectionItemProps {
  field: TemplateSectionFormValues & { id: string };
  sectionIndex: number;
  onRemove: () => void;
  onCreateNewKpi?: (sectionIndex: number, sectionType: CustomTemplateSectionType, kpiName?: string) => void;
}

function useSectionToggle(sectionIndex: number, type: CustomTemplateSectionType) {
  const { setValue } = useFormContext<CustomKpiTemplateFormValues>();

  const liveSectionType = useWatch<CustomKpiTemplateFormValues>({
    name: `sections.${sectionIndex}.sectionType`,
  }) as KpiSection;

  const sections = useWatch<CustomKpiTemplateFormValues, 'sections'>({ name: 'sections' }) ?? [];
  const quantitativeCount = sections.filter(
    (s: TemplateSectionFormValues) => s.type === CustomTemplateSectionType.quantitative
  ).length;

  const toggleDisabled = isSectionToggleDisabled(type, quantitativeCount);

  const handleToggle = useCallback(
    (newValue: KpiSection) => {
      setValue(`sections.${sectionIndex}.sectionType`, newValue, { shouldDirty: true });
    },
    [setValue, sectionIndex]
  );

  return { toggleDisabled, handleToggle, liveSectionType };
}

export function TemplateSectionItem({
  field,
  sectionIndex,
  onRemove,
  onCreateNewKpi,
}: TemplateSectionItemProps) {
  const { toggleDisabled, handleToggle, liveSectionType } = useSectionToggle(sectionIndex, field.type);
  const isQuantitative = field.type === CustomTemplateSectionType.quantitative;

  return (
    <SortableItem id={field.id}>
      {isQuantitative ? (
        <div css={cardCss}>
          <SectionHeader
            section={{ type: field.type, sectionType: liveSectionType }}
            onDelete={onRemove}
            onToggle={handleToggle}
            toggleDisabled={toggleDisabled}
          />
          <SectionContent sectionIndex={sectionIndex} onCreateNewKpi={onCreateNewKpi} />
        </div>
      ) : (
        <SingleKpiSectionRow
          sectionIndex={sectionIndex}
          sectionType={field.type as CustomTemplateSectionType}
          onDelete={onRemove}
          onCreateNewKpi={onCreateNewKpi}
        />
      )}
    </SortableItem>
  );
}
